import { DatePipe, formatDate } from '@angular/common';
import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Contact } from 'src/model/contact.model';
import { DuitLebih } from 'src/model/duitLebih.model';
import { EPF } from 'src/model/epf.model';
import { EPFLimit } from 'src/model/epfLimit.model';
import { Loan } from 'src/model/loan.model';
import { Profile } from 'src/model/profile.model';
import { SettlementCharges } from 'src/model/settlementCharges.model';
import { Status } from 'src/model/status.model';
import { ALERT_TYPE, FILE_TYPE } from '../constants';
import { AlertDialogComponent, AlertType } from './alert-dialog/alert-dialog.component';
import { Enums } from './enums';

export type ArrayDict<T> = Record<string, T[]>;

interface refreshOptions {
  auto?: boolean; // auto refresh after ?ms
  delay?: number; // time in ms
  hard?: boolean; // Ctrl+F5?
}

type ModalClass = 'regularModal' | 'largeModal';

@Injectable({
  providedIn: 'root',
})

export class SharedService {
  private modalService = inject(NgbModal);
  private enums = inject(Enums);
  private datepipe = inject(DatePipe);
  private readonly locale = inject(LOCALE_ID);

  // Add 1 day and minus 1 millisecond to get last millisecond of the day
  readonly oneDayMinusOneMillisecond = (3600 * 1000 * 24) - 1;

  mapToDict<T>(items: T[], keyName: string, valueName: string): Record<string, keyof T> {
    return items.reduce((result, item) => {
      result[item[keyName]] = item[valueName];
      return result;
    }, {} as Record<number, keyof T>);
  }

  groupBy<T>(array: T[], keyName: keyof T): Record<string, T[]> {
    return array.reduce((result, item) => {
      const groupKey = item[keyName] as unknown as string;
      if (!result[groupKey]) result[groupKey] = [];
      result[groupKey].push(item);
      return result;
    }, {} as Record<string, T[]>);
  }

  groupByNumKey<T>(array: T[], keyName: keyof T): Record<number, T[]> { // Order by key
    return array.reduce((result, item) => {
      const groupKey = item[keyName] === undefined ? -1 : Number(item[keyName]);
      if (!result[groupKey]) result[groupKey] = [];
      result[groupKey].push(item);
      return result;
    }, {} as Record<number, T[]>);
  }

  groupByNumKeyAsMap<T>(array: T[], keyName: keyof T): Map<number, T[]> {  // Preserve order in array
    return array.reduce((result, item) => {
      const groupKey = item[keyName] === undefined ? -1 : Number(item[keyName]);
      const items = result.get(groupKey) || [];
      result.set(groupKey, [...items, item])
      return result;
    }, new Map<number, T[]>());
  }

  groupByMultiple<T>(array: T[], keys: (keyof T)[]): Record<string, T[]> {
    return array.reduce((result, item) => {
      const groupKey = keys.map(k => item[k]).join('|');
      if (!result[groupKey]) result[groupKey] = [];
      result[groupKey].push(item);
      return result;
    }, {} as Record<string, T[]>);
  }

  formatMonth(month: number, option: 'short' | 'long' | '2-digit' | 'numeric' | 'narrow') {
    const d = new Date('01-01-2001');
    d.setMonth(month);
    return d.toLocaleDateString(undefined, { month: option });
  }

  getImage(imageLinks: { url: string, weight: number }[]) {
    const totalWeight: number = imageLinks.reduce((sum, { weight }) => sum + weight, 0);
    const random: number = Math.random() * totalWeight;
    let cumulativeWeight = 0;
    for (const { url, weight } of imageLinks) {
      cumulativeWeight += weight;
      if (random <= cumulativeWeight) return this.enums.imagesBaseUrl + url;
    }

    return this.enums.imagesBaseUrl + imageLinks[0].url;
  }

  sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getLastMsOfDate(aDate: Date): Date {
    aDate.setHours(0, 0, 0, 0);
    return new Date(aDate.getTime() + this.oneDayMinusOneMillisecond);
  }

  deepClone<T>(obj: T): T {
    const clone = (obj: any, visited = new Map()): any => {
      if (obj === null || typeof obj !== 'object') return obj;
      if (visited.has(obj)) return visited.get(obj);
      if (obj instanceof Date) return new Date(obj.getTime());
      if (obj instanceof Array) return obj.map(item => clone(item, visited));

      const cloned = Object.create(Object.getPrototypeOf(obj));
      visited.set(obj, cloned);

      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          cloned[key] = clone(obj[key], visited);
        }
      }
      return cloned;
    };

    return clone(obj);
  }


  getListTotalWithKey(list: any[], key: string): number {
    return list.reduce((sum, acc) => {
      return sum + (acc[key] ?? 0);
    }, 0);
  }

  createLocalISODateEncodedString(date: Date) {
    const t = date;
    let tz = t.toString().split("+").pop()
    tz = (tz === undefined ? "Z" : tz.split(" ", 1)[0])
    return encodeURIComponent((new Date(t.getTime() - (t.getTimezoneOffset() * 60000)).toISOString().replace("Z", (tz === "" ? "Z" : "+" + tz.slice(0, 2).concat(":", tz.slice(-2))))));
  }
  getTodayDate() {
    return new Date();
  }

  isSameDate(date1: Date, date2: Date) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    return d1.getTime() === d2.getTime();
  }

  getMaxDateForEPF() {
    //CURRENTLY MAX DATE OF EPF SETTLEMENT IS 3 YEARS FROM TODAY
    let date;

    const today = new Date();

    //MAX DATE COVERED WHOLE MONTH, EG: TODAY 25/7, MAX DATE = WHOLE JULY OF NEXT 2 YEARS
    date = new Date(today.getFullYear() + 2, today.getMonth() + 1, 0);
    return date;
  }

  addBufferDayToEPFMaxDate(days: number) {
    const maxDate = this.getMaxDateForEPF();
    //add {?} days buffer for epf max date
    const addedBuffer = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate() + days);

    const date = new Date(addedBuffer.getFullYear(), addedBuffer.getMonth() + 1, 0);
    return date;
  }

  isNumberKey(evt) {
    const charCode = (evt.which) ? evt.which : evt.keyCode
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode != 46)
      return false;
    return true;
  }

  isNumberKeyAllowNegative(evt) {
    const charCode = (evt.which) ? evt.which : evt.keyCode
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode != 45 && charCode != 46)
      return false;
    return true;
  }

  openModal(content: any, modalClass?: ModalClass, modalOptions?: NgbModalOptions) {
    modalOptions ||= { backdrop: 'static', keyboard: false, animation: false };
    if (modalClass != undefined)
      modalOptions.windowClass = modalClass;
    const modalRef = this.modalService.open(content, modalOptions);

    return modalRef;
  }

  isRoles(roles: number[]) {
    return roles.includes(Number(sessionStorage.getItem('roleId')));
  }

  isBranchAccountHandler() {
    return this.isBookkeepingStaff() || this.isBranchManager()
  }

  isOperatingUser() {
    return this.isBookkeepingStaff() || this.isBranchManager() || this.isStaff()
  }

  isMonitoringUser() {
    return this.isBossAccount() || this.isAdmin() || this.isAuditor() || this.isFinance()
  }

  isBossAccount() {
    const roleId = Number(sessionStorage.getItem('roleId'));
    if (roleId == this.enums.BOSS) {
      return true;
    }

    else
      return false;
  }

  isFinance() {
    return Number(sessionStorage.getItem('roleId')) == this.enums.FINANCE;
  }

  isAdminHead() {
    return Number(sessionStorage.getItem('roleId')) == this.enums.ADMIN_HEAD;
  }

  isAdmin() {
    const roleId = Number(sessionStorage.getItem('roleId'));
    if (roleId == this.enums.ADMIN)
      return true;
    else
      return false;
  }

  isBranchManager() {
    const roleId = Number(sessionStorage.getItem('roleId'));

    if (roleId == this.enums.BRANCH_MANAGER)
      return true;
    else
      return false;
  }

  isExpenseAdmin() {
    const roleId = Number(sessionStorage.getItem('roleId'));
    if (roleId == this.enums.EXPENSE_ADMIN)
      return true;
    else
      return false;
  }

  isAuditor() {
    const roleId = Number(sessionStorage.getItem('roleId'));
    if (roleId == this.enums.AUDITOR)
      return true;
    else
      return false;
  }

  isBookkeepingStaff() {
    const roleId = Number(sessionStorage.getItem('roleId'));
    if (roleId == this.enums.BOOKKEEPING_STAFF)
      return true;
    else
      return false;
  }

  isStaff() {
    const roleId = Number(sessionStorage.getItem('roleId'));

    if (roleId == this.enums.STAFF)
      return true;
    else
      return false;
  }

  async hardRefresh() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    window.location.reload();
  }

  openAlert(message: string, type: AlertType, refresh: boolean = false, refreshOptions?: refreshOptions) {
    const modalRef = this.openModal(AlertDialogComponent);
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.alertType = type;

    if (refresh) {
      const { auto = true, delay = 3000, hard = false } = refreshOptions ?? {};
      if (auto) {
        setTimeout(() => {
          if (hard) this.hardRefresh();
          else window.location.reload();
        }, delay);
      }

      modalRef.componentInstance.okClicked.subscribe(() => {
        if (hard) this.hardRefresh();
        else window.location.reload();
      });
    }
  }

  openAlertCallbackAction(message: string, type: AlertType, callback: () => void) {
    const modalRef = this.openModal(AlertDialogComponent);
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.alertType = type;

    const sub = modalRef.componentInstance.okClicked.subscribe(() => {
      callback();
    });

    modalRef.result.catch(() => {}).finally(() => sub.unsubscribe());
  }

  openErrorMessage(xhr: XMLHttpRequest) {
    const modalRef = this.openModal(AlertDialogComponent);
    modalRef.componentInstance.alertType = ALERT_TYPE.DANGER;
    let errorMessage = "";

    if (xhr.readyState == 4 && xhr.responseText != undefined) {
      errorMessage = xhr.responseText;
    }

    modalRef.componentInstance.message = "Opps, some error occurred. Please try again later = " + errorMessage;
  }

  processStatusCode(status: number[]);
  processStatusCode(status: Status[]);
  processStatusCode(status: any) {
    if (status !== undefined && status.length > 0) {
      const statusCode: number[] = (typeof status[0] == 'object') ? status.map(x => x.StatusCode) : status;
      if (statusCode.some(s => s === this.enums.ACTIVE_LOAN)) return this.enums.ACTIVE_LOAN;
      else if (statusCode.some(s => s === this.enums.WARNING_LOAN)) return this.enums.WARNING_LOAN;
      else if (statusCode.some(s => s === this.enums.BAD_DEBT_LOAN)) return this.enums.BAD_DEBT_LOAN;
      else if (statusCode.some(s => s === this.enums.CP_LOAN)) return this.enums.CP_LOAN;
      else if (statusCode.some(s => s === this.enums.LBD_LOAN)) return this.enums.LBD_LOAN;
      else if (statusCode.some(s => s === this.enums.SETTLED_LOAN)) return this.enums.SETTLED_LOAN;
    } else return null;
  }

  processICNum(icNumber: string) {
    const icNumberFormat = icNumber.replace(/-/g, "",);
    let output = "";
    for (let i = 0; i < icNumberFormat.length; i++) {
      output += icNumberFormat[i];
      if (i == 5 || i == 7)
        output += "-"
    }
    return output;
  }

  sanitizeICNumInput(icNumber: string) {
    return icNumber.trim().replace(/-/g, "",);
  }

  processStringStripInvalidSymbol(str: string) {
    //strip invalid symbol : ';','&'
    return str.replace(/[&;]/g, "");
  }

  checkIfICValid(icNumber: string) {
    if (icNumber != undefined && icNumber != "") {
      const icNumberFormat = icNumber.replace(/-/g, "",);
      const regexp = /^(?:[0-2]\d(?:0\d|1[0-2])(?:[0-2]\d|3[0-1])\d{2}[0-4][0-9]{3})|(?:[3-9]\d(?:0\d|1[0-2])(?:[0-2]\d|3[0-1])\d{2}[5-9]\d{3})$/

      return regexp.test(icNumberFormat);
    }
    else
      return false;
  }

  checkFormJExpired(lastFormJDate: Date) {
    if (lastFormJDate != undefined && lastFormJDate) {
      const monthDiff = new Date().getMonth() - new Date(lastFormJDate).getMonth() +
        (12 * (new Date().getFullYear() - new Date(lastFormJDate).getFullYear()))

      // var difference_time = new Date().getTime() - new Date(lastFormJDate).getTime();
      // var difference_day = difference_time / (1000 * 3600 * 24);

      if (monthDiff >= 9)
        return true
    }
    return false
  }

  getEPFTerm(epfDate: Date) {
    if (epfDate != undefined) {
      epfDate = new Date(epfDate);
      let monthDiff = 0;
      if (epfDate.getFullYear() == new Date().getFullYear()) {
        monthDiff = epfDate.getMonth() - new Date().getMonth();
      }
      else {
        const yearDiff = epfDate.getFullYear() - new Date().getFullYear();
        const epfMonth = epfDate.getMonth() + (yearDiff * 12);
        monthDiff = epfMonth - new Date().getMonth();
      }

      monthDiff = (monthDiff == 0) ? 1 : monthDiff;
      return monthDiff;
    }
  }

  getDiscountedTerm(settleDate: Date, epfDate: Date) {
    if (settleDate != undefined) {
      settleDate = new Date(settleDate);

      let monthDiff = 0;
      if (new Date(epfDate).getFullYear() == new Date(settleDate).getFullYear()) {
        monthDiff = new Date(epfDate).getMonth() - settleDate.getMonth();
      }
      else {
        const yearDiff = new Date(epfDate).getFullYear() - settleDate.getFullYear();
        const months = new Date(epfDate).getMonth() + (yearDiff * 12);
        monthDiff = months - settleDate.getMonth();
      }

      // monthDiff = (monthDiff == 0) ? 1 : monthDiff;
      return monthDiff;
    }
  }

  checkIsSeniorRestrictedCustomer(dob: Date) {
    const minimumRestrictedDate = new Date(dob.getFullYear() + this.enums.MINIMUM_SENIOR_RESTRICTED_AGE, dob.getMonth(), dob.getDate());
    const maximumRestrictedDate = new Date(dob.getFullYear() + this.enums.MAXIMUM_SENIOR_RESTRICTED_AGE, dob.getMonth(), dob.getDate() + this.enums.RESTRICTED_AGE_BUFFER_DAY);

    if (new Date() >= minimumRestrictedDate && new Date() <= maximumRestrictedDate)
      return true;

    else return false;
  }

  checkInvalidCustomerAge(dob: Date) {
    const minimumDOB = new Date(new Date().getFullYear() - this.enums.VALID_CUSTOMER_AGE, new Date().getMonth(), new Date().getDate());

    // make sure customer born before minimum customer dob
    if (dob != undefined && new Date(dob) > minimumDOB)
      return true;
    else return false;
  }

  //guarantor age only consider until YEAR
  checkInvalidGuarantorAge(birthYear: number) {
    const maximumBirthYear = new Date().getFullYear() - this.enums.VALID_GUARANTOR_AGE;

    // make sure customer born before minimum customer dob
    if (birthYear != undefined && birthYear <= maximumBirthYear)
      return true;
    else return false;
  }

  calculateTotalOnHand(loan: Loan) {
    // console.log(loan);
    const duitLebihAmount = this.calculateDuitLebihAmount(loan.Extras);
    // console.log(duitLebihAmount);
    // console.log(loan.Principal, loan.Reserve, loan.ProcessingRate, (loan.Principal * (loan.ProcessingRate / 100)),  loan.StampAmount, loan.ServiceCharge, duitLebihAmount);
    return loan.Principal - loan.Reserve - (loan.Principal * (loan.ProcessingRate / 100)) - loan.StampAmount - loan.ServiceCharge - duitLebihAmount
  }

  roundTo2Dec(num: number) {
    return this.roundToDec(num, 2)
  }

  roundToDec(oriNum: number, decimalPlace: number) {
    const roundNum = Math.pow(10, decimalPlace);
    return Math.round(oriNum * roundNum) / roundNum
  }

  // get the minimum epf amount of each term category
  checkEPFAmountLimitCategory(epfDate: Date) {
    const term = this.getEPFTerm(new Date(epfDate));

    for (let i = 0; i < this.enums.epfAmountCategory.length; i++) {
      if (term <= this.enums.epfAmountCategory[i].UpperLimit && term >= this.enums.epfAmountCategory[i].LowerLimit) {
        return i;
      }
    }
  }

  //get the epf limit category based on their epf date and epf amount
  //must do minimum epf amount check to check the input before proceed to this
  checkEPFLimitCategory(epfDate: Date, epfAmount: number, epfLimitRuleSet?: EPFLimit[]) {
    const term = this.getEPFTerm(new Date(epfDate));
    const epfLimitRule = (epfLimitRuleSet == undefined) ? this.enums.epfLimitList : epfLimitRuleSet;

    for (let i = 0; i < epfLimitRule.length; i++) {
      //if maximum epf amount  & minimum epf amount == undefined, ignore  epf amount check
      const checkMaximumEPFAmount = (epfLimitRule[i].MaximumEPFAmount == undefined) ? true : epfAmount < epfLimitRule[i].MaximumEPFAmount;
      const checkMinimumEPFAmount = (epfLimitRule[i].MinimumEPFAmount == undefined) ? true : epfAmount >= epfLimitRule[i].MinimumEPFAmount;

      if (term <= epfLimitRule[i].UpperLimit && term >= epfLimitRule[i].LowerLimit) {
        if (checkMinimumEPFAmount && checkMaximumEPFAmount)
          return i;
        else if (epfLimitRule[i].IsMinimumnTier)
          return i;
      }
    }
  }

  getEPFLimitPrincipal(epfDate: Date, epfAmount: number, epfLimitRuleSet?: EPFLimit[]) {
    const limitCategory = this.checkEPFLimitCategory(epfDate, epfAmount, epfLimitRuleSet);
    const epfLimitRule = (epfLimitRuleSet == undefined) ? this.enums.epfLimitList : epfLimitRuleSet;

    return this.roundTo2Dec(epfAmount * epfLimitRule[limitCategory].OverallLoanRate);
  }

  getEPFLimitFirstLoan(epfDate: Date, epfAmount: number, epfLimitRuleSet?: EPFLimit[]) {
    const limitCategory = this.checkEPFLimitCategory(epfDate, epfAmount, epfLimitRuleSet);
    const epfLimitRule = (epfLimitRuleSet == undefined) ? this.enums.epfLimitList : epfLimitRuleSet;

    const settlement = this.getEPFLimitSettlement(epfDate, epfAmount, epfLimitRule);
    const maximumPrincipalBySettlementLimit = this.calculatePrincipalBySettlementLeft(settlement, this.getEPFTerm(epfDate), false);

    const firstLoanPrincipalByLimit = this.roundTo2Dec(epfAmount * epfLimitRule[limitCategory].FirstLoanRate);

    return (firstLoanPrincipalByLimit < maximumPrincipalBySettlementLimit) ? firstLoanPrincipalByLimit : maximumPrincipalBySettlementLimit;
  }

  calculateRoundedPrincipal(originalPrincipal: number) {
    let max = 0;
    if (originalPrincipal != undefined) {
      max = originalPrincipal;
    }
    return Math.floor(max / 50) * 50;
  }

  calculateRoundedDuitLebih(principal: number, epfDate: Date, epfAmount: number, originalPrincipal: number) {
    var principal = this.calculateRoundedPrincipal(originalPrincipal);
    const processingCharge = principal * 0.1;
    const ads = principal * 0.02;
    return 100 - ((principal + processingCharge + ads + this.enums.MINIMUM_STAMP + this.enums.MINIMUM_SC) % 100);
  }

  getEPFLimitCashOnHand(epfDate: Date, epfAmount: number, originalPrincipal: number) {
    const principal = this.calculateRoundedPrincipal(originalPrincipal);
    const processingCharge = principal * 0.1;
    const ads = principal * 0.02;
    const duitLebih = this.calculateRoundedDuitLebih(principal, epfDate, epfAmount, originalPrincipal);

    return principal - processingCharge - ads - duitLebih - this.enums.MINIMUM_STAMP - this.enums.MINIMUM_SC;
  }

  getEPFLimitSettlement(epfDate: Date, epfAmount: number, epfLimitRuleSet?: EPFLimit[]) {
    const limitCategory = this.checkEPFLimitCategory(epfDate, epfAmount, epfLimitRuleSet);
    const epfLimitRule = (epfLimitRuleSet == undefined) ? this.enums.epfLimitList : epfLimitRuleSet;

    return this.roundTo2Dec(epfAmount * epfLimitRule[limitCategory].MaximumSettlementRate);
  }

  getEPFElevateSettlement(epfDate: Date, epfAmount: number, epfLimitRuleSet?: EPFLimit[]) {
    const limitCategory = this.checkEPFLimitCategory(epfDate, epfAmount, epfLimitRuleSet);
    const epfLimitRule = (epfLimitRuleSet == undefined) ? this.enums.epfLimitList : epfLimitRuleSet;

    return this.roundTo2Dec(epfAmount * epfLimitRule[limitCategory].ElevatedSettlementRate);
  }

  calculatePreviousEPFLoanPrincipal(loanList: Loan[]) {
    let principalSum = 0;

    if (loanList != undefined) {
      for (let i = 0; i < loanList.length; i++) {
        principalSum += loanList[i].Principal;
      }
    }
    return principalSum;
  }

  calculateSettlementRatio(settlement: number, epfAmount: number) {
    return this.roundTo2Dec((settlement / epfAmount) * 100)
  }

  calculateEPFSettlementAmount(principal: number, interestRate: number, term: number) {
    return principal + (term * principal * (interestRate / 100))
  }

  calculateEPFSettlementAmountWithCharges(loan: Loan) {
    let settlement = loan.Principal + (loan.Term * loan.Principal * (loan.InterestRate / 100));
    // var termLeft = (loan.Balance == undefined) ? loan.Term : this.roundTo2Dec(loan.Balance / loan.MonthlyPayment);

    if (loan.SettlementCharges != undefined)
      settlement += loan.SettlementCharges.Stamp + loan.SettlementCharges.ServiceCharge + (loan.SettlementCharges.Extras || 0) + (loan.SettlementCharges.HandlingCharge || 0);
    else if (loan.SettlementExtra != undefined || loan.SettlementServiceCharge != undefined || loan.SettlementStamp != undefined)
      settlement += loan.SettlementExtra + loan.SettlementServiceCharge + loan.SettlementStamp;

    return settlement;
  }
  calculatePreviousEPFLoanSettlement(loanList: Loan[]) {
    let sum = 0;

    if (loanList != undefined) {
      for (let i = 0; i < loanList.length; i++) {
        const termLeft = (loanList[i].Balance == undefined) ? loanList[i].Term : this.roundTo2Dec(loanList[i].Balance / loanList[i].MonthlyPayment);

        if (loanList[i].LegacySettlement != undefined)
          sum += loanList[i].LegacySettlement;

        else if (loanList[i].Scheme == 'EPF') {
          const previousSettlementWithoutCharge = this.calculateEPFSettlementAmount(loanList[i].Principal, loanList[i].InterestRate, loanList[i].Term)

          if (loanList[i].SettlementCharges == undefined)
            sum += previousSettlementWithoutCharge + loanList[i].SettlementExtra + loanList[i].SettlementServiceCharge + loanList[i].SettlementStamp
          else
            sum += previousSettlementWithoutCharge + loanList[i].SettlementCharges.Extras + loanList[i].SettlementCharges.ServiceCharge + loanList[i].SettlementCharges.Stamp + (loanList[i].SettlementCharges.HandlingCharge || 0)
        }
      }
    }

    return sum;
  }

  calculateRoundedPrincipalByOnHand(cashOnHand: number) {
    const roundedPrincipal = Math.ceil(((cashOnHand + this.enums.MINIMUM_STAMP + this.enums.MINIMUM_SC) / 0.88) / 50) * 50;

    return roundedPrincipal;
  }

  calculateRoundedDLByOnHand(cashOnHand: number) {
    const oriPrincipal = (cashOnHand + this.enums.MINIMUM_STAMP + this.enums.MINIMUM_SC) / 0.88;
    const roundedPrincipal = Math.ceil(oriPrincipal / 50) * 50;

    return Math.round((roundedPrincipal - oriPrincipal) * 0.88);
  }

  calculatePrincipalBySettlementLeft(settlementLeft: number, term: number, isExtra: boolean) {
    let principal
    if (isExtra == true)
      principal = (settlementLeft - (this.enums.MIN_SSC_PER_TERM * term)) / (1 + (term * this.enums.MINIMUM_EXTRA_EPF_RATE) + (this.enums.MININUM_HANDLING_RATE * term))
    else
      principal = (settlementLeft - (this.enums.MIN_SSC_PER_TERM * term)) / (1 + (term * this.enums.MINIMUM_NEW_EPF_RATE));

    return (principal < 0) ? 0 : principal;
  }

  calculateEPFDate(dob: Date) {
    // if customer's birthday falls on 29/2, set the epf date to 28/2
    if (dob.getDate() == 29 && dob.getMonth() == 1)
      dob.setDate(dob.getDate() - 1);

    // var epfTier1Date = new Date(dob.getFullYear() + this.enums.EPF_TIER_1, dob.getMonth(), dob.getDate());
    const epfTier2Date = new Date(dob.getFullYear() + this.enums.EPF_TIER_2, dob.getMonth(), dob.getDate());
    const epfTier3Date = new Date(dob.getFullYear() + this.enums.EPF_TIER_3, dob.getMonth(), dob.getDate());

    if (epfTier2Date > new Date())
      return epfTier2Date;
    else
      return epfTier3Date;
  }

  defineEPFTier(dob: Date) {
    // var epfTier1Date = new Date(dob.getFullYear() + this.enums.EPF_TIER_1, dob.getMonth(), dob.getDate());
    const epfTier2Date = new Date(dob.getFullYear() + this.enums.EPF_TIER_2, dob.getMonth(), dob.getDate());

    if (epfTier2Date > new Date())
      return this.enums.EPF_TIER_2;
    else
      return this.enums.EPF_TIER_3;
  }

  checkIsAllowEPF(dob: Date, epf: EPF, profileId: number, sharedProfileId: number) {
    //if is non shared epf / is main profile only can open epf request
    //share epf request, main must have loan so need to open request from main's end
    if (sharedProfileId == undefined || sharedProfileId == profileId) {
      if (epf != undefined && epf.Date != undefined) {
        //default date might contain time section
        const epfDate = new Date(epf.Date);
        const formattedDate = new Date(epfDate.getFullYear(), epfDate.getMonth(), epfDate.getDate())

        //format date and compare
        return formattedDate <= this.getMaxDateForEPF() && formattedDate >= new Date();
      }

      else {
        const calculatedEPFDate = this.calculateEPFDate(new Date(dob));

        return calculatedEPFDate <= this.getMaxDateForEPF() && calculatedEPFDate >= new Date();
      }
    }
    else
      return false;
  }

  calculateDuitLebihAmount(extras: DuitLebih[]) {
    let duitLebihAmount = 0;
    if (extras != undefined && extras.length > 0) {
      for (let i = 0; i < extras.length; i++) {
        duitLebihAmount += extras[i].Amount;
      }
    }

    return duitLebihAmount;
  }

  checkFileType(contentType: string, filename: string): number {
    if (contentType.includes('image/'))
      return FILE_TYPE.IMAGE;
    if (contentType === 'application/pdf')
      return FILE_TYPE.PDF;
    if (contentType.includes("video/") || (contentType.includes("application/octet-stream") && /\.(mov)$/g.test(filename)))
      return FILE_TYPE.VIDEO;
    return FILE_TYPE.OTHER;
  }

  checkIsPDF(contentType: string) {
    if (contentType == "application/pdf")
      return true;
    else
      return false;
  }

  checkIsVideo(contentType: string, filename: string) {
    if (contentType.includes("video/") == true)
      return true;
    else if (contentType.includes("application/octet-stream") == true) {
      const regexp = /\.(mov)$/g;
      const isMovVideoExt = regexp.test(filename);
      return isMovVideoExt;
    }
    else
      return false;
  }

  checkIfDateIsToday(date: Date) {
    if (date.getFullYear() == new Date().getFullYear() && date.getMonth() == new Date().getMonth() && date.getDate() == new Date().getDate())
      return true;
    else
      return false;
  }

  getMaxCommissionAmount(loan: Loan) {
    const rate = this.getMaxCommissionRate(loan);
    return this.roundTo2Dec(loan.Principal * rate);
  }

  getMaxCommissionRate(loan: Loan) {
    let rate = 0
    if (loan.Scheme == 'EPF') {
      rate = this.enums.EPF_COMMISSION_MAX_RATE;
    }
    else {
      rate = this.enums.OTHER_COMMISSION_MAX_RATE;
    }
    return rate;
  }

  checkCommissionOverpay(loan: Loan, commission: number) {
    const maxCommissionAmount = this.getMaxCommissionAmount(loan);

    if (this.roundTo2Dec(commission) > maxCommissionAmount)
      return true;
    else return false;
  }

  //EXTRA CHARGERS DISTRIBUTION + CALCULATION
  calculateAndReturnSettlementCharges(principal: number, chargeCollected: number, loanTerm: number) {
    const maxTotalCharge = this.calculateMaxTotalExtraCharges(principal, loanTerm);

    let extraExtras = 0;
    if (chargeCollected > maxTotalCharge) {
      extraExtras = chargeCollected - maxTotalCharge;
      chargeCollected = maxTotalCharge;
    }

    let sscAmount = chargeCollected * this.enums.MAX_SSC_OVERCOLLETED;
    if (sscAmount < this.enums.MIN_SSC_PER_TERM * loanTerm)
      sscAmount = this.enums.MIN_SSC_PER_TERM * loanTerm;

    const serviceCharge = Math.ceil(sscAmount * this.enums.SC_PER_SSC);
    const stamp = Math.ceil(sscAmount - serviceCharge);
    const extras = chargeCollected - serviceCharge - stamp + extraExtras;

    return new SettlementCharges(serviceCharge, stamp, extras)
  }

  calculateEPFFirstLoanSettlementCharges(chargeCollected: number, loanTerm: number) {
    const minimumCollectCharge = this.enums.MIN_SSC_PER_TERM * loanTerm;

    const extras = chargeCollected - minimumCollectCharge;
    const serviceCharge = this.enums.MINIMUM_SC * loanTerm;
    const stamp = this.enums.MINIMUM_STAMP * loanTerm;

    return new SettlementCharges(serviceCharge, stamp, extras)
  }

  calculateMaxTotalExtraCharges(principal: number, term: number) {
    return (this.enums.MIN_SSC_PER_TERM + this.enums.SSC_PER_TERM_PER_STEP * (principal - this.enums.MIN_SSC_STARTING_PRINCIPAL) / this.enums.PRINCIPAL_STEP) * term;
  }

  isLoanRelatedType(typeId: number) {
    return typeId == this.enums.PRINCIPAL || typeId == this.enums.REPAYMENT || typeId == this.enums.EXTRA_INTEREST || typeId == this.enums.MONTHLY_INTEREST
      || typeId == this.enums.ARREAR_CHARGES || typeId == this.enums.DISCOUNT
  }

  isLoanRelatedSubype(subTypeId: number) {
    return subTypeId == this.enums.LOAN_EXPENSE_SUBTYPE_ID
  }

  calculateLoanMonthlyPayment(scheme: string, principal: number, principalPaid: number, term: number, interestRate: number) {
    var interestRate = interestRate / 100;
    if (scheme == 'A')
      return (principal - principalPaid) * (interestRate)
    else
      return (principal * interestRate) + (principal / term);
  }

  isEPFDateValid(dob: Date, epfDate: Date) {
    const calculatedEPFDate = this.calculateEPFDate(new Date(dob));
    if (this.datepipe.transform(calculatedEPFDate, 'dd-MM-yyyy') == this.datepipe.transform(new Date(epfDate), 'dd-MM-yyyy'))
      return true;
    else
      return false;
  }

  deduceYearFromIC(icNumber: string) {
    const yearPartFromIC = icNumber.substring(0, 2);
    const currentCentury = new Date().getFullYear().toString().substring(0, 2);
    let constructedYear = Number(currentCentury + yearPartFromIC);

    while (constructedYear > new Date().getFullYear()) {
      constructedYear -= 100;
    }

    return constructedYear;
  }

  isDOBValidWithIC(icNumber: string, dob: Date) {
    if (this.checkIfICValid(icNumber) == true) {
      const yearPartFromIC = icNumber.substring(0, 2);
      const monthPartFromIC = Number(icNumber.substring(2, 4));
      const dayPartFromIC = Number(icNumber.substring(4, 6));

      const currentCentury = new Date().getFullYear().toString().substring(0, 2);
      let constructedYear = Number(currentCentury + yearPartFromIC);
      let constructedDOB = new Date(constructedYear, monthPartFromIC - 1, dayPartFromIC)

      while (constructedDOB > new Date()) {
        constructedYear = constructedYear - 100;
        constructedDOB = new Date(constructedYear, monthPartFromIC - 1, dayPartFromIC)
      }

      if (this.datepipe.transform(constructedDOB, 'dd-MM-yyyy') == this.datepipe.transform(new Date(dob), 'dd-MM-yyyy'))
        return true;
      else
        return false;
    }
    else
      return false;
  }

  calculateInterestRate(monthly: number, principal: number, term: number) {
    const total = monthly * term;
    let interestRate;
    interestRate = (((total - principal) / principal) / term) * 100;

    return interestRate;
  }

  calculateMonthlyPayment(interestRate: number, principal: number, term: number) {
    return (((interestRate / 100) * term * principal) + principal) / term
  }

  processDuitLebihJsonArr(jsonArray: any) {
    const duitLebihList: DuitLebih[] = [];
    jsonArray.forEach(json => {
      const extra = new DuitLebih(json.amount, json.subtype)
      duitLebihList.push(extra);
    });

    return duitLebihList;
  }

  processGuarantorJsonArr(jsonArray: any) {
    const guarantorsInfo: Contact[] = [];
    jsonArray.forEach(json => {
      const guarantors = new Contact(json.id, json.name, json.icNumber, json.number, json.address, json.customerId, json.relation, json.remark)
      guarantorsInfo.push(guarantors);
    });

    guarantorsInfo.sort((a, b) => (a.Name > b.Name) ? -1 : 1);
    return guarantorsInfo;
  }

  checkIsWholeNumber(num: number) {
    if (num - Math.floor(num) != 0)
      return false;
    else
      return true;
  }

  checkIsMainShareProfile(profile: Profile) {
    if (profile.ID == profile.SharedProfileId)
      return true;
    else
      return false;
  }

  getLoggedInUsername() {
    return sessionStorage.getItem("username");
  }

  generateDownloadLink(xhr: XMLHttpRequest, filename: string) {
    const contentType = xhr.getResponseHeader("content-type");

    const data = xhr.response;
    const file = new Blob([data], { type: contentType });

    const downloadLink = document.createElement("a");
    document.body.appendChild(downloadLink);
    const csvUrl = URL.createObjectURL(file);
    downloadLink.href = csvUrl;
    downloadLink.download = filename;
    downloadLink.click();
    URL.revokeObjectURL(downloadLink.href)
    downloadLink.remove();

    // For Firefox
    setTimeout(function () {
      document.body.removeChild(downloadLink);
      // Release resource on disk after triggering the download
      window.URL.revokeObjectURL(data);
    }, 30000);
  }

  calculateTotalIncludeInterest(loan: Loan) {
    return this.roundTo2Dec((loan.InterestRate / 100 * loan.Principal * loan.Term) + loan.Principal);
  }

  calculateActivePrincipal(loanList: Loan[]) {
    let totalPrincipal = 0;
    for (let i = 0; i < loanList.length; i++) {
      //if not settled loan, SKIM A = PRINCIPAL BALANCE, OTHER THAN SKIM A ALL COUNT TOTAL PRINCIPAL
      if (loanList[i].Status != this.enums.SETTLED_LOAN) {
        if (loanList[i].Scheme == "A") {
          totalPrincipal += loanList[i].Principal - loanList[i].PrincipalPaid
        }
        else {
          totalPrincipal += loanList[i].Principal;
        }
      }
    }

    return totalPrincipal
  }

  calculateTotalSettlementBalance(loanList: Loan[]) {
    let totalSettlement = 0;
    for (let i = 0; i < loanList.length; i++) {
      if (loanList[i].Status != this.enums.SETTLED_LOAN) {
        if (loanList[i].Scheme == "A") {
          //if is LEGACY A EPF
          if (loanList[i].IsEPF == true)
            totalSettlement += (loanList[i].LegacySettlement || 0) - loanList[i].PrincipalPaid;

          //if is normal A
          else
            totalSettlement += loanList[i].Principal - loanList[i].PrincipalPaid;
        }
        else {
          //if settlement charges != undefined, this case only can be true when it's epf loan
          // add principal + interest + all left settlement charges - Repayment made
          if (loanList[i].HasSettlementChargesObj)
            totalSettlement += (loanList[i].Principal + (loanList[i].Principal * (loanList[i].InterestRate / 100) * loanList[i].Term))
              + ((loanList[i].SettlementCharges.Extras || 0) + (loanList[i].SettlementCharges.Stamp || 0) + (loanList[i].SettlementCharges.ServiceCharge || 0) + (loanList[i].SettlementCharges.HandlingCharge || 0))
              - loanList[i].Repayment

          //if there's no settlement charges, just add on all the principal + interest + legacy defined settlement charges values - Repayment made
          else
            totalSettlement += (loanList[i].Principal + (loanList[i].Principal * (loanList[i].InterestRate / 100) * loanList[i].Term))
              + ((loanList[i].SettlementExtra || 0) + (loanList[i].SettlementStamp || 0) + (loanList[i].SettlementServiceCharge || 0))
              - loanList[i].Repayment
        }
      }
    }
    return totalSettlement;
  }

  calculateSettlementToDate(loan: Loan, settleDate: Date, epfDate: Date) {
    // var interestTerm = this.getChargeableTerm(new Date(loan.DateCreated), settleDate);

    //if early settlement, interest term = supposed term - the diff of date settle and epf date
    //if normal settlement, then interest term will be full
    const interestTerm = (settleDate < epfDate) ? (loan.Term - (this.getDiscountedTerm(settleDate, epfDate))) : loan.Term;
    const chargeableInterest = (loan.Principal * (loan.InterestRate / 100)) * interestTerm;
    let totalCharges;

    if (loan.HasSettlementChargesObj)
      totalCharges = (loan.SettlementCharges.Extras || 0) + (loan.SettlementCharges.HandlingCharge || 0) + loan.SettlementCharges.Stamp + loan.SettlementCharges.ServiceCharge;
    else
      totalCharges = (loan.SettlementExtra || 0) + (loan.SettlementServiceCharge || 0) + (loan.SettlementStamp || 0);

    const chargeableCharges = (totalCharges / loan.Term) * interestTerm;
    return chargeableInterest + chargeableCharges + loan.Principal;
  }

  isAcctCheckedInBeforeAction(actionDate: Date, latestCheckInDate: Date) {
    //make sure account is correctly checked in before performing any action
    //make sure last checked in date = 1 day before action date (cannot be more than that)
    const actionCheckInDate = this.datepipe.transform(new Date(actionDate.getFullYear(), actionDate.getMonth(), actionDate.getDate() - 1), 'dd-MM-yyyy');
    const formattedLatestCheckInDate = this.datepipe.transform(latestCheckInDate, 'dd-MM-yyyy');

    if (actionCheckInDate == formattedLatestCheckInDate)
      return true
    else
      return false;
  }

  // compare result of 2 strs, only compare alphanumerical value
  //used in clipboard components
  compareResult(compareString: string, currValue: string) {
    if (compareString != "" && currValue != undefined) {
      const valueStr = currValue.replace(/[^a-zA-Z0-9\.\%]/g, "");
      const compareStr = compareString.replace(/[^a-zA-Z0-9\.\%]/g, "");

      if (valueStr == compareStr)
        return true;
    }
    return false;
  }

  //strip time from a date
  stripTime(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  formatDate(aDate: Date, format: string = 'dd-MM-yyyy') {
    return formatDate(new Date(aDate), format, this.locale);
  }
}
